import { useEffect, useState } from "react";
import { GetSearchResults, SearchResultsDTO } from "../../../Api/Search";

function useFetchSearchResults(
    searchTerm: string,
    types: string[],
    excludeEpisodes: boolean,
    take: number = 20,
    skip: number = 0,
    shouldAppendData: boolean = false
) {
    const [results, setResults] = useState<SearchResultsDTO>({ items: [], total: 0 });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        // Don't fetch if the user hasn't typed anything yet
        if (!searchTerm.trim()) {
            setResults({ items: [], total: 0 });
            return;
        }

        const abortController = new AbortController();
        setIsLoading(true);
        setError("");

        (async () => {
            try {
                const data = await GetSearchResults(searchTerm, types, excludeEpisodes, take, skip, abortController);
                if (!abortController.signal.aborted && data) {
                    setResults((prev) => {
                        if (shouldAppendData) {
                            return {
                                items: [...prev.items, ...data.items],
                                total: data.total
                            };
                        }
                        return data;
                    });
                }
            } catch (err: any) {
                if (!abortController.signal.aborted) {
                    setError("Failed to fetch search results.");
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setTimeout(() => {
                        setIsLoading(false);
                    }, 500)
                }
            }
        })();

        // Cleanup function aborts the network request if dependencies change before it finishes
        return () => abortController.abort();
    }, [searchTerm, types.join(','), excludeEpisodes, take, skip, shouldAppendData]);

    return { results, isLoading, error };
}

export default useFetchSearchResults;
